import { useMemo, useCallback, useEffect } from "react";

import { TabPanel } from "@similarweb/ui-components";

import { ScorableTabs } from "app/components/React/ScorableTabs/ScorableTabs";
import { BasicDurations } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useHighLevelMetricsContext } from "../context";
import { useEnrichedMetrics } from "../hooks/hooks";
import type { MetricSummaryData } from "../types";

import { HighLevelMetricsSingularChartWrapper } from "./HighLevelMetricsSingularChartWrapper";

interface IMetricTabsProps {
    enrichmentData: MetricSummaryData;
}
export const MetricTabs = ({ enrichmentData }: IMetricTabsProps) => {
    const { params, selectedMetric, setSelectedMetricId, durations } = useHighLevelMetricsContext();

    const { duration, webSource } = params;
    const is28d = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const { isWindow } = durations?.forAPI || {};

    const { enrichedMetrics } = useEnrichedMetrics(enrichmentData, {
        duration: String(duration),
        webSource: String(webSource),
        isWindow: Boolean(isWindow),
    });

    const tabs = useMemo(() => {
        return enrichedMetrics.map((metric) => {
            const baseTab = {
                id: metric.id,
                name: metric.name,
                getTitle: metric.getTitle,
                metric: metric.name,
                tooltip: metric?.tooltip,
                iconName: metric.iconName,
                formatter: metric.formatter,
                metricComponent: HighLevelMetricsSingularChartWrapper,
                getEndpoint: metric.getEndpoint,
                getChartType: metric.getChartType,
                tooltipProps: metric.tooltipProps,
                apiName: metric.apiName,
                isTabDisable: metric.isTabDisabled,
            };

            const displayValue = metric.formatter(metric.value);
            const title = metric.getTitle({ is28d });

            return {
                ...baseTab,
                title,
                value: displayValue,
                enabled: !metric.isDisabled,
                stringId: metric.id,
            };
        });
    }, [enrichedMetrics, is28d]);

    const setSelected = useCallback(
        (tab: (typeof tabs)[0]) => {
            allTrackers.trackEvent("Tab", "click", `Table/${tab?.title}`);

            if (tab?.stringId) {
                setSelectedMetricId(tab.stringId as any);
                // Update URL parameter for bookmarking
                void proNavigator.updateParams({ selectedMetric: tab.stringId });
            }
        },
        [setSelectedMetricId],
    );

    const visibleTabs = useMemo(() => {
        return tabs.filter((tab) => tab.enabled);
    }, [tabs]);

    const currentSelectedTab = useMemo(() => {
        if (!selectedMetric?.id) {
            return undefined;
        }
        return visibleTabs.find((tab) => tab.id === selectedMetric.id);
    }, [visibleTabs, selectedMetric?.id]);

    useEffect(() => {
        // Only auto-select if no metric is specified in URL and no tab is currently selected
        if (!params.selectedMetric && !currentSelectedTab && visibleTabs.length > 0) {
            setSelected(visibleTabs[0]);
        }
    }, [currentSelectedTab, visibleTabs, setSelected, params.selectedMetric]);

    if (visibleTabs.length === 0) {
        return null;
    }

    return (
        <ScorableTabs
            tabs={visibleTabs}
            selectedTab={currentSelectedTab || visibleTabs[0]}
            setSelected={setSelected}
        >
            {visibleTabs.map((tab) => (
                <TabPanel key={tab.stringId}>
                    <tab.metricComponent />
                </TabPanel>
            ))}
        </ScorableTabs>
    );
};
